import { Injectable } from '@angular/core';
import { GraphqlDBO } from '../class/graphql.dbo.class';

import { ApolloQueryResult, DataProxy, FetchResult, MutationOptions, ObservableQuery, WatchQueryOptions } from '@apollo/client/core';
import { OperationVariables } from '@apollo/client/core/types';
import { ErrorResponse } from '@apollo/client/link/error';
import { Observable as ApolloObservable } from '@apollo/client/utilities';
import { AuthenticationService, SessionService } from '@innovation-os-ng/core/auth';
import { EnvironmentService } from '@innovation-os-ng/core/utils';
import { PersistentStorage } from 'apollo3-cache-persist';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class GqlService {
  private gqlDBOs: { [id: string]: GraphqlDBO } = {};
  private defaultGqlDBO: GraphqlDBO;

  constructor(
    private readonly environmentService: EnvironmentService,
    private readonly sessionService: SessionService,
    private readonly authenticationService: AuthenticationService
  ) {}

  /**
   * Generates an apollo client with in-memory cache store
   *
   * @param dbId
   * @param endPoint
   * @param authLink
   * @param cacheEntitys
   * @param credentials
   * @param persistCache
   * @param errorLink
   * @param possibleTypes
   */
  public createDBO(
    dbId: string,
    endPoint: string,
    authLink?,
    cacheEntitys?: Array<any>,
    credentials?: string,
    persistCache?: PersistentStorage<any>,
    errorLink?: (error: ErrorResponse) => void,
    possibleTypes?
  ) {
    if (persistCache) {
      this.gqlDBOs[dbId] = new GraphqlDBO(
        endPoint,
        this.environmentService,
        this.sessionService,
        this.authenticationService,
        authLink,
        cacheEntitys,
        credentials,
        persistCache,
        errorLink,
        possibleTypes
      );
    } else {
      this.gqlDBOs[dbId] = new GraphqlDBO(
        endPoint,
        this.environmentService,
        this.sessionService,
        this.authenticationService,
        authLink,
        cacheEntitys,
        credentials,
        null,
        errorLink,
        possibleTypes
      );
    }

    if (!this.defaultGqlDBO) {
      this.defaultGqlDBO = this.gqlDBOs[dbId];
    }
  }

  /**
   * Resets and clears the cache store
   *
   * @param dbID
   */
  public resetStore(dbID: string): void {
    if (this.gqlDBOs && this.gqlDBOs[dbID]) {
      this.gqlDBOs[dbID].getClient().resetStore();
    }
  }

  /**
   * Watches the cache store of given query and returns ObservableQuery
   *
   * @param options
   * @param dbId
   */
  public defaultWatchQuery<T, TVariables>(options: WatchQueryOptions<TVariables>, dbId?: string): ObservableQuery<T, TVariables> {
    return dbId ? this.gqlDBOs[dbId].defaultWatchQuery(options) : this.defaultGqlDBO.defaultWatchQuery(options);
  }

  /**
   * Watches the cache store of given query and returns result as zen-observable from @apollo/client/utilities
   *
   * @param options
   * @param dbId
   */
  public watchQuery<T, TVariables>(options: WatchQueryOptions<TVariables>, dbId?: string): ApolloObservable<ApolloQueryResult<T>> {
    return dbId ? this.gqlDBOs[dbId].watchQuery(options) : this.defaultGqlDBO.watchQuery(options);
  }

  /**
   * Resolves mutation and returns result
   *
   * @param options
   * @param dbId
   */
  public mutate<T, TVariables>(options: MutationOptions<T, TVariables>, dbId?: string): Observable<FetchResult<T, Record<string, any>>> {
    return dbId ? this.gqlDBOs[dbId].mutate(options) : this.defaultGqlDBO.mutate(options);
  }

  /**
   * Cache Options
   */

  /**
   * Reads data from the cache and returns the result
   *
   * @param options
   * @param optimistic
   * @param dbId
   */
  public readQuery<T, TVariables = OperationVariables>(
    options: DataProxy.Query<TVariables, T>,
    optimistic?: boolean,
    dbId?: string
  ): T | null {
    return dbId ? this.gqlDBOs[dbId].readQuery(options, optimistic) : this.defaultGqlDBO.readQuery(options, optimistic);
  }

  /**
   * Writes data to the cache store
   *
   * @param options
   * @param dbId
   */
  public writeQuery<TData = any, TVariables = OperationVariables>(
    options: DataProxy.WriteQueryOptions<TData, TVariables>,
    dbId?: string
  ): void {
    return dbId ? this.gqlDBOs[dbId].writeQuery(options) : this.defaultGqlDBO.writeQuery(options);
  }
}
